import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

/**
 * gallera-voice FASE 4 — Central de Ligações (ADMIN).
 *
 * Lista TODAS as ligações de voz de TODOS os atendentes da empresa
 * (GET /voice/history, admin-only no backend) com filtros de período, atendente
 * e conexão, e um player que toca a gravação (GET /voice/recording/:id — proxy/
 * presigned admin-only). Item de menu só p/ admin.
 */

const useStyles = makeStyles((theme) => ({
  filterSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: { minWidth: 720 },
  formControl: { minWidth: 150 },
  statCard: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    height: "100%"
  },
  statValue: { fontSize: 22, fontWeight: "bold", lineHeight: 1.1 },
  statLabel: { fontSize: 12, color: "#666", marginTop: 4 }
}));

const StatCard = ({ value, label, color, cls }) => (
  <Paper variant="outlined" className={cls.statCard}>
    <div className={cls.statValue} style={color ? { color } : undefined}>
      {value}
    </div>
    <div className={cls.statLabel}>{label}</div>
  </Paper>
);

const statusLabel = {
  answered: { text: "Atendida", color: "#28a745" },
  missed: { text: "Perdida", color: "#dc3545" },
  rejected: { text: "Recusada", color: "#e08600" },
  received: { text: "Recebida", color: "#666" }
};

const fmtDuration = (s) => {
  const sec = Number(s || 0);
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

// tempo total legível: 1h 05m / 12m 30s / 45s
const fmtTotal = (s) => {
  const sec = Number(s || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const r = sec % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m) return `${m}m ${String(r).padStart(2, "0")}s`;
  return `${r}s`;
};

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    return format(typeof d === "string" ? parseISO(d) : d, "dd/MM/yyyy HH:mm");
  } catch (_) {
    return String(d);
  }
};

const CentralLigacoes = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [users, setUsers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [audioSrc, setAudioSrc] = useState("");
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [recordingOn, setRecordingOn] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    direction: "",
    userId: "",
    whatsappId: "",
    dateFrom: "",
    dateTo: ""
  });

  // status da gravação (p/ o aviso de LGPD ao cliente)
  useEffect(() => {
    api
      .get("/voice/enabled")
      .then(({ data }) => setRecordingOn(!!data?.record))
      .catch(() => {});
  }, []);

  // carrega atendentes e conexões p/ os filtros (best-effort)
  useEffect(() => {
    api
      .get("/users/list")
      .then(({ data }) => setUsers(Array.isArray(data) ? data : data?.users || []))
      .catch(() => {});
    api
      .get("/whatsapp")
      .then(({ data }) => setConnections(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const buildParams = useCallback(
    (page) => {
      const params = {};
      if (page) {
        params.pageNumber = page;
        params.limit = 50;
      }
      if (filters.status) params.status = filters.status;
      if (filters.direction) params.direction = filters.direction;
      if (filters.userId) params.userId = filters.userId;
      if (filters.whatsappId) params.whatsappId = filters.whatsappId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      return params;
    },
    [filters]
  );

  const doExport = useCallback(
    async (path, filename) => {
      setExporting(true);
      try {
        const resp = await api.get(path, { params: buildParams(null), responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        toastError(err);
      } finally {
        setExporting(false);
      }
    },
    [buildParams]
  );

  const handleExport = useCallback(
    () => doExport("/voice/history/export", "ligacoes.csv"),
    [doExport]
  );
  const handleExportXlsx = useCallback(
    () => doExport("/voice/history/export.xlsx", "ligacoes.xlsx"),
    [doExport]
  );
  const handleExportPdf = useCallback(
    () => doExport("/voice/history/export.pdf", "ligacoes.pdf"),
    [doExport]
  );

  const fetchCalls = useCallback(
    async (page = 1, append = false) => {
      setLoading(true);
      try {
        const { data } = await api.get("/voice/history", { params: buildParams(page) });
        setCount(data?.count || 0);
        setHasMore(!!data?.hasMore);
        if (data?.summary) setSummary(data.summary);
        setCalls((prev) => (append ? [...prev, ...(data?.calls || [])] : data?.calls || []));
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    setPageNumber(1);
    fetchCalls(1, false);
  }, [fetchCalls]);

  const handleFilter = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const loadMore = () => {
    const next = pageNumber + 1;
    setPageNumber(next);
    fetchCalls(next, true);
  };

  const handlePlay = async (call) => {
    if (playingId === call.id) {
      setPlayingId(null);
      setAudioSrc("");
      return;
    }
    try {
      // baixa a gravação autenticada (proxy/presigned) e toca via blob URL
      const resp = await api.get(`/voice/recording/${call.id}`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(resp.data);
      setAudioSrc(url);
      setPlayingId(call.id);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Central de Ligações {count ? `(${count})` : ""}</Title>
      </MainHeader>

      {recordingOn && (
        <Paper
          variant="outlined"
          style={{
            background: "#fff3cd",
            border: "1px solid #ffe69c",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 14,
            color: "#664d03"
          }}
        >
          ⚠️ <b>Aviso de privacidade (LGPD):</b> as ligações da sua empresa estão sendo{" "}
          <b>gravadas</b>. É sua responsabilidade informar e obter o{" "}
          <b>consentimento (termo assinado)</b> dos seus atendentes e clientes sobre a
          gravação das chamadas.
        </Paper>
      )}

      {summary && (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard cls={classes} value={summary.total} label="Total" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard cls={classes} value={summary.answered} label="Atendidas" color="#28a745" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard cls={classes} value={summary.missed} label="Perdidas" color="#dc3545" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard cls={classes} value={summary.rejected} label="Recusadas" color="#e08600" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard cls={classes} value={fmtTotal(summary.totalTalkSeconds)} label="Tempo total" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard cls={classes} value={fmtDuration(summary.avgTalkSeconds)} label="Média/atendida" />
          </Grid>
        </Grid>
      )}

      {summary && summary.byUser && Object.keys(summary.byUser).length > 0 && (
        <Paper variant="outlined" style={{ marginBottom: 16, padding: 8 }}>
          <Typography variant="subtitle2" style={{ padding: "4px 8px" }}>
            Desempenho por atendente
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Atendente</TableCell>
                <TableCell align="center">Ligações</TableCell>
                <TableCell align="center">Tempo total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(summary.byUser)
                .sort((a, b) => b.count - a.count)
                .map((u) => {
                  const nome = u.userId
                    ? users.find((x) => Number(x.id) === Number(u.userId))?.name ||
                      `Atendente #${u.userId}`
                    : "— (não atendida)";
                  return (
                    <TableRow key={String(u.userId ?? "0")}>
                      <TableCell>{nome}</TableCell>
                      <TableCell align="center">{u.count}</TableCell>
                      <TableCell align="center">{fmtTotal(u.talkSeconds)}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper className={classes.filterSection} variant="outlined">
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item>
            <FormControl className={classes.formControl}>
              <InputLabel>Situação</InputLabel>
              <Select value={filters.status} onChange={handleFilter("status")}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="answered">Atendidas</MenuItem>
                <MenuItem value="missed">Perdidas</MenuItem>
                <MenuItem value="rejected">Recusadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl className={classes.formControl}>
              <InputLabel>Direção</InputLabel>
              <Select value={filters.direction} onChange={handleFilter("direction")}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="inbound">Entrada</MenuItem>
                <MenuItem value="outbound">Saída</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl className={classes.formControl}>
              <InputLabel>Atendente</InputLabel>
              <Select value={filters.userId} onChange={handleFilter("userId")}>
                <MenuItem value="">Todos</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl className={classes.formControl}>
              <InputLabel>Conexão</InputLabel>
              <Select value={filters.whatsappId} onChange={handleFilter("whatsappId")}>
                <MenuItem value="">Todas</MenuItem>
                {connections.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField
              label="De"
              type="date"
              value={filters.dateFrom}
              onChange={handleFilter("dateFrom")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Até"
              type="date"
              value={filters.dateTo}
              onChange={handleFilter("dateTo")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => fetchCalls(1, false)}
              disabled={loading}
            >
              Filtrar
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExport}
              disabled={exporting || loading}
            >
              {exporting ? "Exportando…" : "Exportar CSV"}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportXlsx}
              disabled={exporting || loading}
            >
              {exporting ? "Exportando…" : "Exportar Excel"}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleExportPdf}
              disabled={exporting || loading}
            >
              {exporting ? "Exportando…" : "Exportar PDF"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {audioSrc && (
        <Paper variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio src={audioSrc} controls autoPlay style={{ width: "100%" }} />
        </Paper>
      )}

      <Paper variant="outlined">
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell align="center">Direção</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Conexão</TableCell>
              <TableCell>Atendente</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell align="center">Duração</TableCell>
              <TableCell align="center">Gravação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.map((c) => {
              const st = statusLabel[c.status] || { text: c.status, color: "#666" };
              return (
                <TableRow key={c.id}>
                  <TableCell>{fmtDate(c.createdAt)}</TableCell>
                  <TableCell align="center">
                    {c.direction === "outbound" ? "📤 Saída" : "📥 Entrada"}
                  </TableCell>
                  <TableCell>
                    {c.contact?.name || "-"}
                    <br />
                    <Typography variant="caption" color="textSecondary">
                      {c.contact?.number || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.whatsapp?.name || "-"}</TableCell>
                  <TableCell>{c.user?.name || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={st.text}
                      size="small"
                      style={{ backgroundColor: st.color, color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell align="center">{fmtDuration(c.durationSeconds)}</TableCell>
                  <TableCell align="center">
                    {c.recordingFile ? (
                      <Button size="small" color="primary" onClick={() => handlePlay(c)}>
                        {playingId === c.id ? "Parar" : "Ouvir"}
                      </Button>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && calls.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhuma ligação no período/filtros.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CircularProgress size={24} />
          </div>
        )}
        {hasMore && !loading && (
          <div style={{ textAlign: "center", padding: 12 }}>
            <Button onClick={loadMore}>Carregar mais</Button>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default CentralLigacoes;
